import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Save, ShieldOff } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

export function SettingsTrackingPage() {
  const utils = trpc.useUtils();
  const query = trpc.settings.getTrackingIgnoreIps.useQuery();
  const [value, setValue] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (query.data) {
      setValue(query.data.value);
      setDirty(false);
    }
  }, [query.data]);

  const save = trpc.settings.setTrackingIgnoreIps.useMutation({
    onSuccess: () => {
      toast.success("Tracking ignore list updated");
      setDirty(false);
      utils.settings.getTrackingIgnoreIps.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to save"),
  });

  const entries = query.data?.entries ?? [];

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6">
      <header className="mb-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Eye className="h-4 w-4 text-muted-foreground" />
          Quote landing tracking
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          When a client opens a proposal landing page (
          <code className="rounded bg-muted px-1 py-0.5 text-xs">/p/&lt;token&gt;</code>), the visit is
          logged as an event and the opportunity card shows a grey checkmark. Add internal IPs below to
          exclude your team's visits from the tracking events and view counters.
        </p>
      </header>

      <div className="rounded-lg border p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-sm font-medium">Ignored IP addresses</span>
          <Badge variant="secondary" className="bg-zinc-100 text-zinc-600">
            {entries.length} entr{entries.length === 1 ? "y" : "ies"}
          </Badge>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          One per line — or separate with commas or spaces. Both IPv4 (
          <code className="rounded bg-muted px-1 py-0.5">203.0.113.42</code>) and IPv6 are supported.
          Matching is exact; <code className="rounded bg-muted px-1 py-0.5">::ffff:</code> IPv4-mapped
          IPv6 prefixes are stripped automatically.
        </p>
        <Textarea
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setDirty(true);
          }}
          placeholder={"203.0.113.42\n198.51.100.7"}
          rows={8}
          className="font-mono text-sm"
          disabled={query.isLoading}
        />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {entries.length === 0 ? (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <ShieldOff className="h-3.5 w-3.5" />
                All visits will be tracked (no ignored IPs configured).
              </span>
            ) : (
              <span>
                Active ignored IPs: {entries.map((ip) => (
                  <code key={ip} className="ml-1 rounded bg-muted px-1 py-0.5 text-[11px]">
                    {ip}
                  </code>
                ))}
              </span>
            )}
          </div>
          <Button
            size="sm"
            onClick={() => save.mutate({ value })}
            disabled={!dirty || save.isPending}
            className="gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
